import React, { useCallback, useMemo, useRef, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';

// HELPERS
import { useListData } from 'helpers/getListData';
import useResizeContainer from '../helpers/useResizeContainer';

// CORE COMPONENTS
import Sprite from 'core/tools/Sprite';

// LOCAL COMPONENTS
import LinkCell from '../tools/LinkCell';
import NumberCell from '../tools/NumberCell';
import TableRowDnD from './layout/TableRowDnD';
import TableHeader from './layout/TableHeader';

// STYLES
import styles from './tableDnD.module.scss';

// DRAG HANDLE CELL
const DragHandleCell = ({ rowId }) => {
  const { attributes, listeners } = useSortable({ id: rowId });

  return (
    <button className={styles.draggable} {...attributes} {...listeners}>
      <Sprite use="drag-handle" size="lg" className="text-gray-700" />
    </button>
  );
}

export default function TableDnD({
  autoSizeHeight = true, //
  className,
  onRowDrop,
  placeholder,
  showNumber,
  ...props
}) {
  // REFS
  const outerContainerRef = useRef(null);

  // HOOKS / PROPS
  const {
    result: data = [], //
    columns: rawColumns
  } = Object.assign(useListData() || {}, props);

  const containerHeight = useResizeContainer(outerContainerRef, autoSizeHeight);

  // STATE
  const [records, setRecords] = useState(() => data.map(p => ({ ...p, id: p.id || uuidv4() })));

  // MEMOS
  const columns = useMemo(() => {
    let transformed = rawColumns.map(({ component, id, key, label, to, width, ...c }) => {
        const column = {
          ...c,
          header: label,
          id: id || key,
          accessorKey: key,
          size: width,
          to,
          enableResizing: false
        };
        if (component) column.cell = component;
        else if (to) column.cell = LinkCell;
        return column;
      }
    );

    const dragHandleColumn = {
      id: 'drag-handle',
      header: 'Move',
      cell: ({ row }) => <DragHandleCell rowId={row.id} />,
      size: 50,
      enableResizing: false,
      className: 'text-center',
      headerClassName: 'text-center'
    };

    if (showNumber) {
      const rowNumberColumn = {
        header: '#',
        id: 'row',
        headerClassName: 'flex-grow-0',
        className: 'flex-grow-0',
        cell: ({ row: { index = 0 } }) => <NumberCell className="text-start" value={index + 1} />,
        size: 45,
        enableResizing: false
      };

      transformed = [rowNumberColumn, ...transformed];
    }

    transformed = [dragHandleColumn, ...transformed];

    return transformed;
  }, [rawColumns, showNumber]);

  const dataIds = useMemo(
    () => records?.map(({ id }) => id),
    [records]
  );

  // REACT TABLE
  const table = useReactTable({
    data: records,
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  // CALLBACKS
  const handleDragEnd = useCallback(event => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      let newIndex;
      let newRecords;
      setRecords(currentRecords => {
        const oldIndex = dataIds.indexOf(active.id);
        newIndex = dataIds.indexOf(over.id);
        newRecords = arrayMove(currentRecords, oldIndex, newIndex);
        return newRecords;
      });
      onRowDrop({ dropIndex: newIndex, records: newRecords, row: newRecords[newIndex] })
    }
  }, [dataIds, onRowDrop]);

  return (
    <div
      ref={outerContainerRef}
      className={clsx('position-relative d-flex flex-column flex-grow-1', styles.container, className)}
    >
      <div style={{ height: containerHeight, paddingRight: '2rem', overflow: 'auto' }}>
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <table className="table table-striped flex-grow-1 w-100 mb-0">
            <TableHeader groups={table.getHeaderGroups()} />
            <tbody className="position-relative">
              <SortableContext
                items={dataIds}
                strategy={verticalListSortingStrategy}
              >
                {table.getRowModel().rows.map(row => (
                  <TableRowDnD key={row.id} row={row} placeholder={placeholder} />
                ))}
              </SortableContext>
            </tbody>
          </table>
        </DndContext>
      </div>
    </div>
  );
}
